import * as THREE from 'three';
import { loadThreeModel } from './loadThreeModel';
import { getCssVar } from '@/utils/cssVars';

export interface SnapshotOptions {
  width?: number;
  height?: number;
  background?: string | number | null;
  useVertexColors?: boolean;
}

export async function generateModelSnapshot(
  file: File,
  { width = 300, height = 300, background = null, useVertexColors = false }: SnapshotOptions = {},
): Promise<string> {
  const geom = await loadThreeModel(file);
  geom.computeBoundingBox();
  const bbox = geom.boundingBox!;
  const size = bbox.getSize(new THREE.Vector3()).length();
  const center = bbox.getCenter(new THREE.Vector3());

  const material = new THREE.MeshStandardMaterial({
    flatShading: true,
    vertexColors: useVertexColors,
    color: useVertexColors ? undefined : 0xdddddd,
    side: THREE.DoubleSide,
  });

  const mesh = new THREE.Mesh(geom, material);
  mesh.position.sub(center);

  const scene = new THREE.Scene();
  scene.add(mesh);
  scene.add(new THREE.AmbientLight(0x404040, 40));
  const dir = new THREE.DirectionalLight(0xffffff, 0.8);
  dir.position.set(1, 1, 1).normalize();
  scene.add(dir);

  const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, size * 10);
  camera.position.set(0, 0, size * 1.5);
  camera.lookAt(0, 0, 0);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setSize(width, height);

  let bgColor = background;
  if (typeof background === 'string') {
    bgColor = getCssVar(background);
  }

  bgColor && renderer.setClearColor(bgColor, 1);
  renderer.render(scene, camera);

  const dataURL = renderer.domElement.toDataURL('image/png');

  renderer.dispose();
  geom.dispose();
  material.dispose();

  return dataURL;
}
